import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { PermissionsEnum } from '@/api/Identity/Types/PermissionsEnum'
import { PageData } from '@/api/Pagination/Types'
import { SortDescriptor } from '@progress/kendo-data-query/dist/npm/sort-descriptor'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridColumnProps,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid'
import { MarketingCampaignDetailDto } from 'api/Trading-Accounts/Types/MarketingCampaignDetailDto'
import { MarginBonusResultDto } from 'api/Trading-Accounts/Types/MarketingCampaignResultsDto'
import { Pager } from 'components/components/Pager'
import { Modal } from 'components/CustomModal/modal'
import { FilterType } from 'components/Filter'
import { getCampaignResultFields } from 'components/Filter/CampaignFields/getCampaignResultFields'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { RemoveMarginBonusCreditForm } from 'components/MarginBonus/RemoveMarginBonusCreditForm'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { useAppDispatch, useAppSelector } from 'store'
import { CompositeFilterDescriptor, GridFilterActionState } from 'store/grid'
import { pageableSettings } from 'store/grid/grid.slice'
import { fetchMarginBonusResultStatus } from 'store/trading-accounts/trading-accounts.actions'
import { PageQuery } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { AuthSessionContext } from 'utils/AuthContext'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { sleep } from 'utils/sleep'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { CampaignTab } from './CampaignSlug'
import { CampaignNavigation } from './Navigation/CampaignNavigation'
import { CampaignResultsItem } from './results/providers/CampaignResultsProvider'
import { useCampaignResultsProvider } from './results/useCampaignResultsProvider'

export interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

interface OuterProps {
  data: MarketingCampaignDetailDto
  columns: GridColumn[]
  sortQuery: SortDescriptor[]
  normalizeSortQuery?: (sortDescriptors: SortDescriptor[]) => SortDescriptor[]
}

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  return {
    fetchMarginBonusResultStatus: async () =>
      dispatch(fetchMarginBonusResultStatus(tradingAccountsApiClient)),
  }
}

export const CampaignDetailsResultsPage: React.FC<OuterProps> = ({
  data,
  normalizeSortQuery,
  ...props
}) => {
  const provider = useCampaignResultsProvider(data.type.id, data.id)

  const [marginBonusRemovalModalVisible, setMarginBonusRemovalModalVisible] = useState(false)
  const [selectedCampaignResultItem, setSelectedCampaignResultItem] =
    useState<CampaignResultsItem | null>(null)
  const [campaignResults, setCampaignResults] = useState<CampaignResultsItem[]>([])
  const [tableData, setTableData] = useState<TableStateContextData>({})
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []
  const walletsApiClient = useApiClient(WalletsApiClient)

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)
  const { fetchMarginBonusResultStatus } = useMapDispatchToProps()
  const resultsExport = useMemo(
    () => provider.mapExport(campaignResults),
    [campaignResults, provider]
  )

  const marginBonusResultStatus = useAppSelector(
    (state) => state.tradingAccounts.marginBonusResultStatus
  )

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (
          gridFilterState: GridFilterActionState
        ): Promise<PageData<CampaignResultsItem[]>> => {
          const response = await provider.fetch({
            search: getQuery(gridFilterState.filter),
            sort: getSort(props.sortQuery),
            pageIndex: gridFilterState.pageIndex ?? 1,
            pageSize: gridFilterState.take ?? gridState.pageSize ?? 10,
          })

          if (isEmpty(marginBonusResultStatus)) await fetchMarginBonusResultStatus()
          if (response?.items) setCampaignResults(response.items)
          setTableData({ ...tableData, meta: response })

          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<CampaignResultsItem[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )
    const response = await callback(gridFilterState)
    setTableData({ ...tableData, meta: response })

    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({ ...response, total, sort: props.sortQuery })
    }

    gridStatusActions.setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null =>
      buildSortQuery(
        normalizeSortQuery ? normalizeSortQuery(sortDescriptors ?? gridState.sort) : props.sortQuery
      ),
    [gridState.sort, normalizeSortQuery, props.sortQuery]
  )

  const refreshGrid = useCallback(
    async (options?: {
      pageIndex?: number
      pageSize?: number
      sort?: SortDescriptor[]
      filter?: CompositeFilterDescriptor
    }): Promise<PageData<CampaignResultsItem[]>> => {
      const response = await provider.fetch({
        search: options?.filter ? getQuery(options.filter) : getQuery(),
        sort: options?.sort ? getSort(options.sort) : getSort(),
        pageIndex: options?.pageIndex ?? gridState.pageIndex,
        pageSize: options?.pageSize ?? gridState.pageSize ?? gridState.take,
      })

      if (response?.items) setCampaignResults(response.items)
      setTableData({ ...tableData, meta: response })

      return response
    },
    [getQuery, getSort, gridState.pageIndex, gridState.pageSize, gridState.take, provider] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const filterFetch = useCallback(
    async (query?: PageQuery): Promise<void> => {
      const response = await provider.fetch({
        search: typeof query?.search === 'string' ? query.search : getQuery(),
        sort: query?.sort ?? getSort(),
        pageIndex: 1,
        pageSize: gridState.pageSize ?? 10,
      })

      if (response?.items) setCampaignResults(response.items)
      setTableData({ ...tableData, meta: response })
      gridActions.setGridFilters({
        ...gridState,
        take: gridState.take ?? 10,
        skip: 0,
        total: response.totalResults,
        pageIndex: 1,
      })
    },
    [data.id, getQuery, gridActions, gridState.pageSize, gridState.take, provider] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const handleGridPageChange = async (page: number) => {
    const response = await refreshGrid({ pageIndex: page, pageSize: gridState.take })
    gridActions.setGridFilters({ ...gridState, total: response.totalResults, pageIndex: page })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    const response = await refreshGrid({ pageIndex: 1, pageSize })
    gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      pageSize,
      take: pageSize,
      total: response.totalResults,
    })
  }

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({ ...gridState, sort: event.sort })
    await refreshGrid({ sort: event.sort, filter: gridState.filter })
  }

  const handleManualMarginBonusOut = async (amount: number) => {
    const marginBonusResultItem = selectedCampaignResultItem as MarginBonusResultDto

    await walletsApiClient.manualMarginBonusOut({
      amount: amount,
      comment: 'Manual Credit Removal',
      transactionTypeId: 663,
      tradingAccountId: marginBonusResultItem?.tradingAccount?.id,
      walletId: marginBonusResultItem?.walletId,
      isManualMarginBonusOut: true,
      // bonusLabelId: data.marginBonusConfiguration?.bonusLabel?.id ?? 0,
    })

    await sleep(2000)
    await refreshGrid()
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  function handleRemoveMarginBonusCredit(dataItem: CampaignResultsItem) {
    setMarginBonusRemovalModalVisible(true)
    setSelectedCampaignResultItem(dataItem)
  }

  return (
    <>
      {marginBonusRemovalModalVisible && (
        <Modal
          visible={marginBonusRemovalModalVisible}
          onClose={() => setMarginBonusRemovalModalVisible(false)}
          width={320}
        >
          <RemoveMarginBonusCreditForm
            credit={(selectedCampaignResultItem as MarginBonusResultDto).credit ?? 0}
            onClose={() => setMarginBonusRemovalModalVisible(false)}
            onSubmit={handleManualMarginBonusOut}
          />
        </Modal>
      )}
      <CampaignNavigation data={data} tab={CampaignTab.RESULTS} />
      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={FilterType.CampaignMarginBonusResults}
        filterFields={[...getCampaignResultFields(data.type.id)]}
        getQuery={getQuery}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <TableWithExport data={resultsExport} columns={props.columns}>
          <Grid
            {...gridState}
            resizable
            onSortChange={handleSortChange}
            sortable={provider.supportsSort && !!normalizeSortQuery}
            data={campaignResults}
            sort={gridState.sort}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            pager={() => (
              <Pager
                onPageChange={handleGridPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take}
                skip={gridState.skip}
              />
            )}
            pageable={pageableSettings}
            style={{ zIndex: 0 }}
          >
            {props.columns
              .filter((c) => c.show)
              .map((column, idx) => (
                <Column key={idx + 2} {...column} />
              ))}
            <Column
              width={50}
              cell={(gridCellProps: GridCellProps) => (
                <td>
                  <MoreButton
                    items={[
                      {
                        name: 'Remove Credit',
                        disabled:
                          !hasPermission(permissions, PermissionsEnum.CanRemoveMarginBonusCredit) ||
                          (gridCellProps.dataItem as MarginBonusResultDto)?.credit <= 0,
                        onClick: () => {
                          handleRemoveMarginBonusCredit(gridCellProps.dataItem)
                        },
                      },
                    ]}
                  />
                </td>
              )}
            />
          </Grid>
        </TableWithExport>
      </TableStateContext.Provider>
    </>
  )
}
